// ═══════════════════════════════════════════════════════════════════════════
// BITÁCORA DE PUBLICACIONES — espejo frontend
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec completo: docs/bitacora-publicaciones.md
//
// Colección: productoBitacora (top-level)
// Shape del evento:
//   {
//     empresaId, productoId,
//     publicacionRef: { plataformaId, externalItemId } | null,
//     tipo,                  // ver TIPOS_EVENTO abajo
//     fecha,                 // serverTimestamp del momento de detección
//     fechaInicio,           // sólo eventos con duración (ad, promo)
//     fechaFin,              // null mientras está activo; se llena al cerrar
//     valorAntes, valorDespues,
//     fuente: 'cowork' | 'ml-sync' | 'tn-sync' | 'manual',
//     taskId?, userId?,
//     plataformaAds?,        // sólo eventos ad — 'meli'|'meta'|'google'|'tn-promociones'
//     ventasAtribuidas?,     // se calcula al cerrar evento con duración
//     acos?, estado?         // 'pendiente'|'cerrado' (eventos con duración)
//   }
//
// Plan-gating: la bitácora se activa por el endpoint `bitacora-publicaciones`
// del catálogo `syncEndpoints`. Ver `empresaTieneBitacora()` abajo.
//
// Backend: triggers Firestore + cron `cerrarEventosPendientes` (functions/
// syncAdapters/mercadoLibre/bitacora-*.js, pendiente F1.2). Cliente NUNCA
// escribe directo (rules block).
// ═══════════════════════════════════════════════════════════════════════════

import {
  collection, doc, getDocs, getDoc, query, where, orderBy, limit,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { createLogger } from './logger'
import { planDeEmpresa } from './planes'
import { CANAL_ML } from './filtrosCanal'

const log = createLogger('bitacora')

// Endpoint id del catálogo `syncEndpoints` que prende/apaga la feature.
export const BITACORA_ENDPOINT_ID = 'bitacora-publicaciones'

// ───────────────────────────────────────────────────────────────────────────
// Catálogo de tipos de evento. Coincide 1:1 con los enum del backend.
// ───────────────────────────────────────────────────────────────────────────

// Cada entrada lleva `iconName` (nombre exportado de lucide-react). El consumer
// importa el icono y lo renderiza. No metemos emojis ni componentes acá para
// que el módulo siga siendo plain JS sin dependencias de React.
export const TIPOS_EVENTO = {
  // Precio
  precio_subido:        { label: 'Precio aumentado',         color: 'amber',   iconName: 'TrendingUp',   categoria: 'precio',   duracion: false },
  precio_bajado:        { label: 'Precio bajado',            color: 'emerald', iconName: 'TrendingDown', categoria: 'precio',   duracion: false },
  // Promo
  promo_activada:       { label: 'Promoción activada',       color: 'blue',    iconName: 'Tag',          categoria: 'promo',    duracion: true  },
  promo_desactivada:    { label: 'Promoción desactivada',    color: 'zinc',    iconName: 'BadgeMinus',   categoria: 'promo',    duracion: false },
  // Ad
  ad_activado:          { label: 'Publicidad activada',      color: 'cyan',    iconName: 'Zap',          categoria: 'ad',       duracion: true  },
  ad_desactivado:       { label: 'Publicidad desactivada',   color: 'zinc',    iconName: 'ZapOff',       categoria: 'ad',       duracion: false },
  // Contenido
  titulo_actualizado:       { label: 'Título actualizado',       color: 'violet', iconName: 'Type',       categoria: 'contenido', duracion: false },
  descripcion_actualizada:  { label: 'Descripción actualizada',  color: 'violet', iconName: 'FileText',   categoria: 'contenido', duracion: false },
  atributos_actualizados:   { label: 'Atributos actualizados',   color: 'violet', iconName: 'ListChecks', categoria: 'contenido', duracion: false },
  // Stock
  reposicion_stock:     { label: 'Reposición de stock',     color: 'green',   iconName: 'PackagePlus',  categoria: 'stock',    duracion: false },
  // Condiciones de venta (10-jul-2026 — cuarentena por condiciones, Publicidad)
  // Color 'blue': la paleta de COLOR_CLASSES/COLOR_HEX de los consumers
  // (BitacoraTimelineList/GraficoModal) no tiene 'sky' — fallback sería zinc.
  envio_actualizado:    { label: 'Envío actualizado',        color: 'blue',    iconName: 'Truck',        categoria: 'condiciones', duracion: false },
  cuotas_actualizadas:  { label: 'Cuotas actualizadas',      color: 'blue',    iconName: 'CreditCard',   categoria: 'condiciones', duracion: false },
  // Manual
  tarea_completada:     { label: 'Tarea completada (CoWork)', color: 'purple', iconName: 'CheckCircle2', categoria: 'manual',  duracion: false },
}

export function metadataTipo(tipo) {
  return TIPOS_EVENTO[tipo] || { label: tipo, color: 'zinc', iconName: 'Circle', categoria: 'otro', duracion: false }
}

// ───────────────────────────────────────────────────────────────────────────
// Gating: ¿la empresa tiene el endpoint activo?
// ───────────────────────────────────────────────────────────────────────────
//
// Lee el plan de `empresas/{eid}` (`planId || plan`, `planDeEmpresa`) + `planes/{plan}.syncConfig['bitacora-publicaciones']`
// + override `empresas/{eid}.syncConfigOverride['bitacora-publicaciones']`.
//
// Devuelve `true` si la bitácora está activa, `false` si está off o si los
// docs no se pueden leer (fail-closed para frontend — empty state seguro).
//
// Uso típico:
//   const activa = await empresaTieneBitacora(empresaId)
//   if (!activa) return <EmptyStateUpgrade />
export async function empresaTieneBitacora(empresaId) {
  if (!empresaId) return false
  try {
    const empSnap = await getDoc(doc(db, 'empresas', empresaId))
    if (!empSnap.exists()) return false
    const emp = empSnap.data() || {}
    if (emp.syncActivo === false) return false

    // Override per-empresa gana sobre el plan.
    const override = emp.syncConfigOverride?.[BITACORA_ENDPOINT_ID]
    if (override) return override.modo !== 'off'

    const planId = planDeEmpresa(emp)
    const planSnap = await getDoc(doc(db, 'planes', planId))
    if (!planSnap.exists()) return false
    const plan = planSnap.data() || {}
    if (plan.syncActivo === false) return false

    const cfg = plan.syncConfig?.[BITACORA_ENDPOINT_ID]
    if (!cfg) return false  // plan sin entrada → off (a diferencia del backend
                            // que en este caso tira fallback permisivo: acá
                            // somos más estrictos porque la UI tiene que
                            // mostrar empty state seguro).
    return cfg.modo !== 'off'
  } catch (err) {
    console.warn('[empresaTieneBitacora] error leyendo gating:', err)
    return false  // fail-closed
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────

/**
 * Lista eventos de la bitácora de un producto (todos los canales/publicaciones).
 * @param {string} empresaId
 * @param {string} productoId
 * @param {Object} [opts]
 * @param {number} [opts.limite=100]  — cap superior (default 100)
 * @returns {Promise<Array>}
 */
export async function listarEventosProducto(empresaId, productoId, { limite = 100 } = {}) {
  if (!empresaId || !productoId) return []
  const q = query(
    collection(db, COLLECTIONS.PRODUCTO_BITACORA),
    where('empresaId', '==', empresaId),
    where('productoId', '==', productoId),
    orderBy('fecha', 'desc'),
    limit(limite),
  )
  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

/**
 * Lista eventos de una publicación específica (filtrados por externalId).
 * Útil para la timeline en la ficha de la pub.
 *
 * MIGRADO A CANÓNICO (28-may-2026, Bloque 5+):
 * El trigger canónico `onPublicacionCanonicaBitacora` escribe el campo plano
 * como `publicacionExternalId` (no `publicacionExternalItemId` legacy). Esta
 * función filtra por el campo nuevo. Los eventos viejos escritos por el trigger
 * legacy `onMlPublicacionBitacora` (apagado el 28-may) usaban el field name
 * viejo — esos eventos quedan sin retornar acá. Si se necesita retro-compat,
 * agregar un getDocs adicional con el field viejo y mergear.
 */
export async function listarEventosPublicacion(empresaId, productoId, externalId, { limite = 100, variationId = null } = {}) {
  if (!empresaId || !productoId || !externalId) return []
  // Si llega variationId, sumamos un where adicional para filtrar a esa
  // variante. Sin variationId trae todos los eventos de la pub (incluidos
  // los de cualquier variación). Bloque 5+ (28-may-2026).
  const constraints = [
    where('empresaId', '==', empresaId),
    where('productoId', '==', productoId),
    where('publicacionExternalId', '==', String(externalId)),
  ]
  if (variationId != null) {
    constraints.push(where('variationId', '==', variationId))
  }
  constraints.push(orderBy('fecha', 'desc'))
  constraints.push(limit(limite))
  const q = query(collection(db, COLLECTIONS.PRODUCTO_BITACORA), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

// ───────────────────────────────────────────────────────────────────────────
// Cuarentena por condiciones (10-jul-2026, Publicidad piloto)
// ───────────────────────────────────────────────────────────────────────────
//
// Regla de Mariano: un ad pausado NO se vuelve a sugerir activar hasta que
// algo MATERIAL de la publicación haya cambiado DESPUÉS de la pausa — precio,
// contenido (título/descripción/atributos), condiciones (envío/cuotas) o una
// promo. Stock explícitamente excluido (reponer no cambia por qué no vendía).
// Sin historia de pausa en la bitácora → sin cuarentena (no inventamos
// historia; el régimen arranca desde donde arrancó el registro).

export const TIPOS_CAMBIO_MATERIAL = [
  'precio_subido', 'precio_bajado',
  'titulo_actualizado', 'descripcion_actualizada', 'atributos_actualizados',
  'envio_actualizado', 'cuotas_actualizadas',
  'promo_activada',   // cambia el precio efectivo → cuenta como cambio
]

// Labels cortos para el badge "↩ ... desde la pausa" del dashboard.
export const CAMBIO_MATERIAL_LABELS = {
  precio_subido:           'subió el precio',
  precio_bajado:           'bajó el precio',
  titulo_actualizado:      'cambió el título',
  descripcion_actualizada: 'cambió la descripción',
  atributos_actualizados:  'cambiaron atributos',
  envio_actualizado:       'cambió el envío',
  cuotas_actualizadas:     'cambiaron las cuotas',
  promo_activada:          'se activó una promo',
}

function _fechaMs(evt) {
  const f = evt?.fecha
  return f?.toMillis?.()
    || (f instanceof Date ? f.getTime() : null)
    || (typeof f === 'string' ? new Date(f).getTime() : null)
    || null
}

/**
 * Estado puro de cuarentena a partir de los eventos de UNA publicación.
 * Exportada para poder testearla sin Firestore.
 *
 * @param {Array} eventos — docs de productoBitacora de la publicación
 * @returns {{ enCuarentena: boolean, pausadaEnMs: number|null,
 *             cambioPosterior: {tipo, fechaMs}|null, cambiosPosteriores: number }}
 */
export function computarCuarentena(eventos) {
  const evts = (eventos || [])
    .map(e => ({ ...e, _ms: _fechaMs(e) }))
    .filter(e => e._ms != null)
    .sort((a, b) => a._ms - b._ms)

  // Último evento de ciclo de ad. Si el más reciente es un ad_activado, la
  // pausa que conocíamos ya no rige (el ad volvió) → sin cuarentena.
  let ultimoAd = null
  for (const e of evts) {
    if (e.tipo === 'ad_activado' || e.tipo === 'ad_desactivado') ultimoAd = e
  }
  if (!ultimoAd || ultimoAd.tipo !== 'ad_desactivado') {
    return { enCuarentena: false, pausadaEnMs: null, cambioPosterior: null, cambiosPosteriores: 0 }
  }

  const pausadaEnMs = ultimoAd._ms
  const cambios = evts.filter(e =>
    TIPOS_CAMBIO_MATERIAL.includes(e.tipo) && e._ms > pausadaEnMs
  )
  const ultimo = cambios.length > 0 ? cambios[cambios.length - 1] : null
  return {
    enCuarentena: cambios.length === 0,
    pausadaEnMs,
    cambioPosterior: ultimo ? { tipo: ultimo.tipo, fechaMs: ultimo._ms } : null,
    cambiosPosteriores: cambios.length,
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Cooldown de acciones (Velocidad de Consumo, report gqpLaPCkeyOj9uvmF5xg)
// ───────────────────────────────────────────────────────────────────────────
//
// Una sobrada cuyo producto ya tuvo publicidad o promo hace poco se «descansa»:
// no se vuelve a sugerir la misma acción. La bitácora es infraestructura
// (decisión del 10-jul-2026, functions/syncCore/syncGating.js
// `bitacoraEscrituraHabilitada`); la vista de Velocidad todavía aplica el
// cooldown sólo con plan, hasta el PR 3c de ese report.
// Cuenta la ejecución real que trae el sync de ML, no la intención: un
// `tarea_completada` no cuenta.

// Plataforma de ads de Mercado Libre en los eventos (`plataformaAds`).
export const PLATAFORMA_ADS_ML = 'meli'
// Eventos anteriores a que existieran `plataformaAds` / `publicacionRef`: su
// único escritor era el sync de ML, así que se leen como de ML.
const PLATAFORMA_ADS_EVENTO_LEGACY = PLATAFORMA_ADS_ML
const CANAL_EVENTO_LEGACY = CANAL_ML

const TIPOS_COOLDOWN_AD = ['ad_activado', 'ad_desactivado']
const TIPOS_COOLDOWN_PROMO = ['promo_activada', 'promo_desactivada']
// Los tipos que cuentan para el cooldown: también filtran la query que los lee.
export const TIPOS_COOLDOWN = [...TIPOS_COOLDOWN_AD, ...TIPOS_COOLDOWN_PROMO]

/**
 * Productos con evento de ad o de promo desde `desdeMs`, por plataforma.
 * Pura: el corte de la ventana entra por parámetro.
 *
 * @param {Array} eventos — docs de productoBitacora (de cualquier tipo)
 * @param {{ desdeMs: number }} opts
 * @returns {{ ad: Map<string, Set<string>>, promo: Map<string, Set<string>> }}
 *   productoId → plataformas de ads (`ad`) o canales de venta (`promo`) con evento
 */
export function cooldownDesdeBitacora(eventos, { desdeMs }) {
  const ad = new Map()
  const promo = new Map()
  const anotar = (mapa, productoId, clave) => {
    const set = mapa.get(productoId) || new Set()
    set.add(clave)
    mapa.set(productoId, set)
  }
  for (const e of (eventos || [])) {
    const fechaMs = _fechaMs(e)
    if (!fechaMs || fechaMs < desdeMs || !e.productoId) continue
    if (TIPOS_COOLDOWN_AD.includes(e.tipo)) {
      anotar(ad, e.productoId, e.plataformaAds || PLATAFORMA_ADS_EVENTO_LEGACY)
    } else if (TIPOS_COOLDOWN_PROMO.includes(e.tipo)) {
      anotar(promo, e.productoId, e.publicacionRef?.plataformaId || CANAL_EVENTO_LEGACY)
    }
  }
  return { ad, promo }
}

/**
 * Carga el estado de cuarentena post-pausa para un conjunto de publicaciones
 * (itemIds de ML). Una query de igualdad por generación de campo plano
 * (canónico `publicacionExternalId` + legacy `publicacionExternalItemId`) por
 * item — sin orderBy para no requerir índices compuestos. Chunked de a 10.
 *
 * NOTA gating: esto es INFRAESTRUCTURA (decisión 10-jul-2026) — se consulta
 * sin mirar el plan. Si la empresa no tiene eventos (gate de escritura estuvo
 * off, o historia previa al registro), devuelve enCuarentena=false para todo.
 *
 * @param {string} empresaId
 * @param {string[]} itemIds
 * @param {Object} [opts]
 * @param {number} [opts.max=80] — cap de items consultados por llamada
 * @returns {Promise<Map<string, ReturnType<typeof computarCuarentena>>>}
 */
export async function cargarCuarentenaPausas(empresaId, itemIds, { max = 80 } = {}) {
  const resultado = new Map()
  if (!empresaId || !Array.isArray(itemIds) || itemIds.length === 0) return resultado

  const ids = [...new Set(itemIds.map(String))].slice(0, max)

  async function eventosDeItem(itemId) {
    const [snapCanon, snapLegacy] = await Promise.all([
      getDocs(query(
        collection(db, COLLECTIONS.PRODUCTO_BITACORA),
        where('empresaId', '==', empresaId),
        where('publicacionExternalId', '==', itemId),
        limit(250),
      )),
      getDocs(query(
        collection(db, COLLECTIONS.PRODUCTO_BITACORA),
        where('empresaId', '==', empresaId),
        where('publicacionExternalItemId', '==', itemId),
        limit(250),
      )),
    ])
    const porId = new Map()
    for (const d of [...snapCanon.docs, ...snapLegacy.docs]) porId.set(d.id, d.data())
    return [...porId.values()]
  }

  const CHUNK = 10
  for (let i = 0; i < ids.length; i += CHUNK) {
    const grupo = ids.slice(i, i + CHUNK)
    const res = await Promise.all(grupo.map(async (itemId) => {
      try {
        return [itemId, computarCuarentena(await eventosDeItem(itemId))]
      } catch (err) {
        log.warn('cargarCuarentenaPausas: error en item', itemId, err)
        return [itemId, { enCuarentena: false, pausadaEnMs: null, cambioPosterior: null, cambiosPosteriores: 0 }]
      }
    }))
    for (const [k, v] of res) resultado.set(k, v)
  }
  return resultado
}
